
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function ServicesPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const corporateServices = [
    { titleKey: 'servicesPage.corporate.item1.title', descKey: 'servicesPage.corporate.item1.desc' },
    { titleKey: 'servicesPage.corporate.item2.title', descKey: 'servicesPage.corporate.item2.desc' },
    { titleKey: 'servicesPage.corporate.item3.title', descKey: 'servicesPage.corporate.item3.desc' },
    { titleKey: 'servicesPage.corporate.item4.title', descKey: 'servicesPage.corporate.item4.desc' },
    { titleKey: 'servicesPage.corporate.item5.title', descKey: 'servicesPage.corporate.item5.desc' },
  ];

  const individualServices = [
    { titleKey: 'servicesPage.individual.item1.title', descKey: 'servicesPage.individual.item1.desc' },
    { titleKey: 'servicesPage.individual.item2.title', descKey: 'servicesPage.individual.item2.desc' },
    { titleKey: 'servicesPage.individual.item3.title', descKey: 'servicesPage.individual.item3.desc' },
    { titleKey: 'servicesPage.individual.item4.title', descKey: 'servicesPage.individual.item4.desc' },
    { titleKey: 'servicesPage.individual.item5.title', descKey: 'servicesPage.individual.item5.desc' },
  ];

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />
      
      {/* Hero Section with Animated Gradient */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-[#1A3A5C] to-[#0D2137] animate-gradient"></div>
        
        {/* Moving Color Overlay */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-gold/10 animate-gradient"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gold/5 to-transparent animate-gradient" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 75% 25%, rgba(201, 169, 98, 0.2) 0%, transparent 50%)`,
          }}></div>
        </div>
        
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
          <div className={`flex items-center gap-2 text-sm mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Link to="/" className="text-gray-400 hover:text-gold transition-colors cursor-pointer">{t('servicesPage.breadcrumb.home')}</Link>
            <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-s-line text-gray-500`}></i>
            <span className="text-gold">{t('servicesPage.breadcrumb.services')}</span>
          </div>
          
          <div className="max-w-3xl">
            <div className={`flex items-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="w-12 h-px bg-gold"></div>
              <span className="text-gold text-sm font-medium tracking-widest uppercase">{t('servicesPage.hero.label')}</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-7xl text-white leading-tight mb-6">
              {t('servicesPage.hero.title1')} <span className="text-gold">{t('servicesPage.hero.title2')}</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed">
              {t('servicesPage.hero.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Services Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          {/* Service for Companies */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center mb-20 md:mb-32">
            <div className={`space-y-6 md:space-y-8 ${isRTL ? 'lg:order-2' : 'order-2 lg:order-1'}`}>
              <div className={`inline-flex items-center gap-2 px-4 py-2 bg-gold/10 rounded-full ${isRTL ? 'flex-row-reverse' : ''}`}>
                <i className="ri-building-line text-gold"></i>
                <span className="text-gold text-sm font-medium">{t('servicesPage.corporate.label')}</span>
              </div>
              
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-navy">
                {t('servicesPage.corporate.title1')} <span className="text-gold">{t('servicesPage.corporate.title2')}</span>
              </h2>
              
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                {t('servicesPage.corporate.description')}
              </p>

              <div className="space-y-4">
                {corporateServices.map((item, index) => (
                  <div key={index} className={`flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gold/5 transition-colors duration-300 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                    <div className="w-10 h-10 animated-gradient-box rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-double-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy mb-1 text-sm md:text-base">{t(item.titleKey)}</h4>
                      <p className="text-gray-600 text-xs md:text-sm">{t(item.descKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={`relative ${isRTL ? 'lg:order-1' : 'order-1 lg:order-2'}`}>
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/services11.jpeg"
                  alt="Corporate Services"
                  className="w-full h-[400px] md:h-[600px] object-cover object-center"
                />
              </div>
              <div className={`absolute -bottom-6 ${isRTL ? '-right-6' : '-left-6'} w-24 h-24 md:w-32 md:h-32 border-2 border-gold/30 rounded-3xl`}></div>
            </div>
          </div>

          {/* Service for Individuals */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
            <div className={`relative ${isRTL ? 'lg:order-2' : ''}`}>
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="services2.webp"
                  alt="Individual Services"
                  className="w-full h-[400px] md:h-[600px] object-cover object-center"
                />
              </div>
              <div className={`absolute -bottom-6 ${isRTL ? '-left-6' : '-right-6'} w-24 h-24 md:w-32 md:h-32 border-2 border-gold/30 rounded-3xl`}></div>
            </div>

            <div className={`space-y-6 md:space-y-8 ${isRTL ? 'lg:order-1' : ''}`}>
              <div className={`inline-flex items-center gap-2 px-4 py-2 bg-gold/10 rounded-full ${isRTL ? 'flex-row-reverse' : ''}`}>
                <i className="ri-user-star-line text-gold"></i>
                <span className="text-gold text-sm font-medium">{t('servicesPage.individual.label')}</span>
              </div>
              
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-navy">
                {t('servicesPage.individual.title1')} <span className="text-gold">{t('servicesPage.individual.title2')}</span>
              </h2>
              
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                {t('servicesPage.individual.description')}
              </p>

              <div className="space-y-4">
                {individualServices.map((item, index) => (
                  <div key={index} className={`flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gold/5 transition-colors duration-300 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                    <div className="w-10 h-10 animated-gradient-box rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-double-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy mb-1 text-sm md:text-base">{t(item.titleKey)}</h4>
                      <p className="text-gray-600 text-xs md:text-sm">{t(item.descKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-navy via-navy-light to-navy">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-6">
            {t('servicesPage.cta.title1')} <span className="text-gold">{t('servicesPage.cta.title2')}</span> {t('servicesPage.cta.title3')}
          </h2>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10">
            {t('servicesPage.cta.description')}
          </p>
          <Link
            to="/contact"
            className={`inline-flex items-center gap-3 px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-gold to-gold-dark text-navy font-semibold rounded-lg hover:shadow-lg hover:shadow-gold/20 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm md:text-base ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            {t('servicesPage.cta.button')} <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line`}></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
